// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyInteraction, TyInteractionChannel } from "@/types";
import { dueState, formatDateTime, pressToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/** Seven channels, four drawings — the same mapping `InteractionCard` uses, kept in step by hand. */
const CHANNEL_ICONS: Record<TyInteractionChannel, TyIconName> = {
    call: "phone",
    whatsapp: "phone",
    sms: "document",
    email: "document",
    visit: "building",
    catalog: "eye",
    other: "more",
};

type Props = { interaction: TyInteraction; onOpen: () => void };

/**
 * A follow-up as one tile in Home's row, next to the counts.
 *
 * Deliberately **not** `InteractionCard`, which is the full timeline row — five marks, an outcome
 * chip, a summary, a face. In a scrolling row of fixed tiles that card is three times the height of
 * everything beside it, and a row whose cards are different shapes stops being scannable.
 *
 * So this says only what you need to act: who, how you are reaching them, and when it is owed.
 */
export default function FollowUpCard({ interaction, onOpen }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const due = dueState(interaction);
    const who = typeof interaction.customer === "object" && interaction.customer ? interaction.customer.name : t.crm.general;

    return (
        <article
            className="follow-up-card"
            data-tone={due === "overdue" ? "late" : "soon"}
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
        >
            <span className="follow-up-card__icon">
                <Icon name={CHANNEL_ICONS[interaction.channel]} size={20} />
            </span>

            <div className="follow-up-card__text">
                <strong className="follow-up-card__who">{who}</strong>
                <span className="follow-up-card__when">
                    {t.crm.channels[interaction.channel]} · {formatDateTime(interaction.date, lang)}
                </span>
            </div>
        </article>
    );
}
